import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Divider,
  Radio,
  Space,
  Alert,
  Row,
  Col,
  Switch,
  message,
} from "antd";
import {
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import apiRequest from "src/common/utils/apiRequest";
import LoadingPreview from "src/common/components/LoadingPreview";

const { Title, Text } = Typography;

const AccountSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<number>(0);

  const onFinish = async () => {
    setSaving(true);
    try {
      await form.validateFields();

      const API_REQUEST = new apiRequest(
        "user/profile/setting/security/edit",
        true
      );

      const values = {
        ...form.getFieldsValue(),
        two_factor_method: twoFactorMethod,
      };

      await API_REQUEST.send(
        values,
        (response: { status: string; message: string }) => {
          if (response.status === "success") {
            message.success(response.message);
          } else {
            message.error(response.message);
          }
        }
      );
    } catch (errorInfo: any) {
      message.error(errorInfo.errorFields[0].errors[0]);
    }
    setSaving(false);
  };

  useEffect(() => {
    const getData = () => {
      setLoading(true);
      const API_REQUEST = new apiRequest(
        "user/profile/setting/security/get",
        true
      );

      API_REQUEST.send(
        {},
        (response: { status: string; data: any; message: string }) => {
          if (response.status === "success") {
            form.setFieldsValue(response.data);
            setTwoFactorMethod(
              response.data.two_factor_method
                ? response.data.two_factor_method
                : 0
            );
            setLoading(false);
          } else {
            message.error(response.message);
          }
        }
      );
    };

    getData();
  }, []);

  const handleTwoFactorChange = (value: number) => {
    setTwoFactorMethod(value);
  };

  return (
    <div>
      <Title level={3} style={{ marginTop: "0" }}>
        Account Settings
      </Title>
      <Text type="secondary">Manage your account security settings</Text>

      <Divider />
      {loading ? (
        <LoadingPreview />
      ) : (
        <>
          <Card title="Password" style={{ marginBottom: "24px" }}>
            <Form
              form={form}
              name="password_change"
              onFinish={onFinish}
              layout="vertical"
            >
              <Form.Item
                name="old_password"
                label="Old Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your old password!",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Old Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="New Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your new password!",
                  },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters!",
                  },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="New Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="repeat_password"
                label="Confirm New Password"
                dependencies={["newPassword"]}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Please confirm your new password!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("new_password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("The two passwords do not match!")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm New Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card
            title="Two-Factor Authentication"
            style={{ marginBottom: "24px" }}
          >
            <Alert
              message="Two-factor authentication adds an additional layer of security to your account."
              type="info"
              showIcon
              style={{ marginBottom: "16px" }}
            />

            <Form.Item name="two_factor_type" label="2FA Method">
              <Radio.Group
                disabled
                onChange={(e) => handleTwoFactorChange(e.target.value)}
                value={twoFactorMethod}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Radio.Button
                    value="0"
                    style={{ textAlign: "left", width: "100%" }}
                  >
                    <Space>
                      <SafetyOutlined />
                      <span>Disabled</span>
                    </Space>
                  </Radio.Button>

                  <Radio.Button
                    value="1"
                    style={{ textAlign: "left", width: "100%" }}
                  >
                    <Space>
                      <MailOutlined />
                      <span>Email Authentication</span>
                    </Space>
                  </Radio.Button>

                  <Radio.Button
                    value="2"
                    style={{ textAlign: "left", width: "100%" }}
                  >
                    <Space>
                      <MobileOutlined />
                      <span>Authenticator App</span>
                    </Space>
                  </Radio.Button>
                </Space>
              </Radio.Group>
            </Form.Item>

            {twoFactorMethod == 1 && (
              <Alert
                message="You'll need to verify your email address to enable this method."
                type="warning"
                showIcon
                style={{ marginTop: "16px" }}
              />
            )}

            {twoFactorMethod == 2 && (
              <Alert
                message="Scan QR code with Google Authenticator or similar app"
                type="warning"
                showIcon
                style={{ marginTop: "16px" }}
              />
            )}

            {twoFactorMethod != 0 && (
              <Button type="primary" style={{ marginTop: "16px" }}>
                {twoFactorMethod == 1 ? "Verify Email" : "Setup Authenticator"}
              </Button>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default AccountSettings;
